import React, { useState } from "react";

import moment from "moment-timezone";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrashAlt } from "@fortawesome/pro-light-svg-icons";
import { faExclamationTriangle } from "@fortawesome/pro-solid-svg-icons";

import Button from "ui/Button";
import RevealableCopyIcon from "ui/RevealableCopyIcon";
import Grid from "ui/Grid";
import Loading from "ui/Loading";
import ModalContainer from "ui/ModalContainer";
import Panel from "ui/Panel";

import { formatDateShort } from "utils/format";
import { useSetFlash } from "providers/WithFlashes";

import {
  AccessTokensDocument,
  AccessTokensQuery,
  useAccessTokensQuery,
} from "./gql/Query.generated";
import {
  useCreateAccessTokenMutation,
  useRevokeAccessTokenMutation,
} from "./gql/Mutation.generated";
import PanelSection from "ui/PanelSection";

type ScopeType =
  AccessTokensQuery["getOrganizationDetails"]["accessTokenScopes"][number];
type AccessTokenType = AccessTokensQuery["getAccessTokens"][number];

// Options offered for the expiry dropdown, mirroring the presets other vendors settle on
const EXPIRY_OPTIONS: { label: string; days: number | null }[] = [
  { label: "30 days", days: 30 },
  { label: "60 days", days: 60 },
  { label: "90 days", days: 90 },
  { label: "1 year", days: 365 },
  { label: "No expiration", days: null },
];

const DEFAULT_EXPIRY_DAYS = 90;

export const AccessTokensPanel = ({
  organizationSlug,
}: {
  organizationSlug: string;
}) => {
  const setFlash = useSetFlash();
  const { data, loading, error } = useAccessTokensQuery({
    variables: { organizationSlug },
  });
  const [revokeToken] = useRevokeAccessTokenMutation();
  const [showCreateToken, setShowCreateToken] = useState(false);

  if (loading || error) {
    return <Loading error={!!error} />;
  }

  // Names are not unique, so the prefix and last use tell two tokens apart
  const handleRevoke = (accessToken: AccessTokenType) => {
    const { id, name, tokenPrefix, lastUsedAt } = accessToken;
    const lastUsed = lastUsedAt
      ? `last used ${formatDateShort(moment.unix(lastUsedAt))}`
      : "never used";
    if (
      !window.confirm(
        `Revoke the access token "${name}" (${tokenPrefix}…, ${lastUsed})?`,
      )
    ) {
      return;
    }
    revokeToken({
      variables: { id },
      onCompleted: () => {
        setFlash("success", `Revoked the access token "${name}".`);
      },
      onError: (error) => {
        setFlash("error", error.message);
      },
      refetchQueries: [
        { query: AccessTokensDocument, variables: { organizationSlug } },
      ],
    });
  };

  return (
    <>
      <Panel title="Personal Access Tokens">
        <Grid
          data={data.getAccessTokens}
          defaultSortBy="createdAt"
          noRowsText="You have not created any personal access tokens for this organization."
          columns={[
            { field: "name", header: "Name", width: "1fr" },
            {
              field: "tokenPrefix",
              header: "Token",
              width: "140px",
              renderer: function TokenCell({ fieldData }) {
                return <span className="font-mono">{fieldData}…</span>;
              },
            },
            {
              field: "scopes",
              header: "Scopes",
              width: "220px",
              title: ({ fieldData }) => fieldData.join(", "),
              renderer: function ScopesCell({ fieldData }) {
                return <>{fieldData.join(", ")}</>;
              },
            },
            {
              field: "expiresAt",
              header: "Expires",
              width: "120px",
              nullValue: "Never",
              renderer: function ExpiresAtCell({ fieldData, rowData }) {
                return (
                  <span
                    className={rowData.expired ? "text-[#CA1515]" : undefined}
                  >
                    {formatDateShort(moment.unix(fieldData))}
                    {rowData.expired && " (expired)"}
                  </span>
                );
              },
            },
            {
              field: "lastUsedAt",
              header: "Last Used At",
              width: "120px",
              nullValue: "Never",
              renderer: function LastUsedAtCell({ fieldData }) {
                return formatDateShort(moment.unix(fieldData));
              },
            },
            {
              field: "createdAt",
              header: "Created At",
              width: "120px",
              renderer: function CreatedAtCell({ fieldData }) {
                return formatDateShort(moment.unix(fieldData));
              },
            },
            {
              field: "id",
              header: "",
              width: "40px",
              renderer: function RevokeCell({ rowData }) {
                return (
                  <Button
                    onClick={() => handleRevoke(rowData)}
                    bare
                    title="Revoke"
                  >
                    <FontAwesomeIcon
                      icon={faTrashAlt}
                      className="text-[#CA1515] cursor-pointer"
                    />
                  </Button>
                );
              },
            },
          ]}
        />
        <PanelSection>
          <button
            className="btn btn-success"
            onClick={() => setShowCreateToken(true)}
          >
            Create Access Token
          </button>
        </PanelSection>
      </Panel>
      {showCreateToken && (
        <CreateAccessTokenModal
          organizationSlug={organizationSlug}
          scopes={data.getOrganizationDetails.accessTokenScopes}
          onDismiss={() => setShowCreateToken(false)}
        />
      )}
    </>
  );
};

const CreateAccessTokenModal = ({
  organizationSlug,
  scopes,
  onDismiss,
}: {
  organizationSlug: string;
  scopes: ScopeType[];
  onDismiss: () => void;
}) => {
  const setFlash = useSetFlash();
  const [name, setName] = useState("");
  // Shown in place of the form once created, the only time the token is available
  const [createdToken, setCreatedToken] = useState<string | null>(null);
  const [checkedOptional, setCheckedOptional] = useState<Set<string>>(
    () => new Set(),
  );
  const [expiresInDays, setExpiresInDays] = useState<number | null>(
    DEFAULT_EXPIRY_DAYS,
  );
  const [createToken, { loading }] = useCreateAccessTokenMutation();

  const requiredScopes = scopes.filter((scope) => scope.required);
  const optionalScopes = scopes.filter((scope) => !scope.required);

  // write_workbooks implies read: keep the two checkboxes in sync so the user
  // can see what they're actually granting.
  const toggleOptional = (scopeName: string, checked: boolean) => {
    setCheckedOptional((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(scopeName);
        if (scopeName === "write_workbooks") {
          next.add("read");
        }
      } else {
        next.delete(scopeName);
        if (scopeName === "read") {
          next.delete("write_workbooks");
        }
      }
      return next;
    });
  };

  const handleCreate = () => {
    createToken({
      variables: {
        organizationSlug,
        name,
        scopes: [
          ...requiredScopes.map((scope) => scope.name),
          ...checkedOptional,
        ],
        expiresInDays,
      },
      onCompleted: (data) => {
        setCreatedToken(data.createAccessToken.token);
      },
      onError: (error) => {
        setFlash("error", error.message);
      },
      refetchQueries: [
        { query: AccessTokensDocument, variables: { organizationSlug } },
      ],
    });
  };

  if (createdToken) {
    return (
      <ModalContainer
        title="Access token created"
        layout="centered"
        onClose={onDismiss}
      >
        <p>
          <FontAwesomeIcon icon={faExclamationTriangle} className="mr-2" />
          Copy this token now. It is not shown again once you close this dialog.
        </p>
        <div className="font-mono break-all">
          {createdToken} <RevealableCopyIcon content={createdToken} />
        </div>
        <div className="mt-4">
          <button className="btn btn-success" onClick={onDismiss}>
            Done
          </button>
        </div>
      </ModalContainer>
    );
  }

  return (
    <ModalContainer
      title="Create New Access Token"
      layout="centered"
      onClose={onDismiss}
    >
      <div>
        <p>
          A token accesses the pganalyze API as you, limited to this
          organization.
        </p>
        <div className="form-group">
          <label htmlFor="access_token_name">
            Name <span className="text-[#c00]">*</span>
          </label>
          <input
            type="text"
            id="access_token_name"
            className="form-control"
            maxLength={200}
            required
            placeholder="e.g. Local development"
            value={name}
            onChange={(evt) => setName(evt.target.value)}
          />
        </div>
        <div className="my-4 p-4 bg-[#f9fafb] rounded-md form-group">
          <p className="font-semibold mb-2">Permissions to grant:</p>
          {requiredScopes.map((scope) => (
            <label key={scope.name} className="block !font-normal">
              <input type="checkbox" checked disabled className="!mr-2" />
              <span className="font-semibold">{scope.name}</span>
              {": "}
              {scope.label}
              <span className="font-semibold italic"> (required)</span>
            </label>
          ))}
          {optionalScopes.map((scope) => (
            <label key={scope.name} className="block !font-normal">
              <input
                type="checkbox"
                checked={checkedOptional.has(scope.name)}
                onChange={(evt) =>
                  toggleOptional(scope.name, evt.target.checked)
                }
                className="!mr-2"
              />
              <span className="font-semibold">{scope.name}</span>
              {": "}
              {scope.label}
              {scope.name === "write_workbooks" && (
                <span className="font-semibold italic"> (requires read)</span>
              )}
            </label>
          ))}
        </div>
        <p className="text-sm text-[#595959]">
          Access is further limited by your account permissions. For example, if
          you can only view certain servers, this token will have the same
          restriction.
        </p>
        <div className="form-group mt-4">
          <label htmlFor="access_token_expiry">Expiration</label>
          <select
            id="access_token_expiry"
            className="form-control"
            value={expiresInDays ?? ""}
            onChange={(evt) =>
              setExpiresInDays(
                evt.target.value === "" ? null : Number(evt.target.value),
              )
            }
          >
            {EXPIRY_OPTIONS.map((option) => (
              <option key={option.label} value={option.days ?? ""}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
        <div className="mt-4">
          <button
            type="button"
            className="btn btn-success mr-4"
            disabled={loading || name.trim() === ""}
            onClick={handleCreate}
          >
            Create new Access Token
          </button>
          <Button bare onClick={onDismiss} variant="link">
            Cancel
          </Button>
        </div>
      </div>
    </ModalContainer>
  );
};
